import { Component } from '@theme/component';
import { escapeHtml } from '@theme/text-utils';
import { compareSearchspringSizeValues, CONSTANTS } from '@theme/searchspring-core';
import { sortByColorOrder } from '@theme/searchspring-filter-logic';

const { COLORS: COLORS_FIELD, COLOR_GROUPS: COLOR_GROUPS_FIELD, SIZES: SIZES_FIELD } = CONSTANTS.FILTER_FIELDS;

/**
 * Counter used to mint a unique id per dropdown, so each trigger can point at its own
 * panel with aria-controls. The desktop and mobile filter lists are both in the DOM, so
 * ids derived from the filter field alone would collide.
 */
let filterInstanceCount = 0;

/**
 * Sort filter values for a given field. Active (selected) values
 * are promoted to the top of the list. Within each group the Size field
 * gets numeric sorting and the Color field the admin's color order.
 *
 * @param {string} field
 * @param {Array<{value: string, active?: boolean}>} values
 * @param {Map<string, number>} colorOrder - lowercase color code → position
 * @returns {Array<{value: string, active?: boolean}>}
 */
function sortFilterValues(field, values, colorOrder) {
  let sorted = values.slice();
  if (field === SIZES_FIELD) {
    sorted.sort((a, b) => compareSearchspringSizeValues(a.value, b.value));
  } else if (field === COLORS_FIELD) {
    sorted = sortByColorOrder(sorted, colorOrder);
  }

  // Partition: active values first, then inactive, preserving order within each group
  const active = sorted.filter(v => v.active);
  const inactive = sorted.filter(v => !v.active);
  return [...active, ...inactive];
}

/**
 * Clone a <template> by ID from the closest section-level ancestor.
 * Falls back to document-level lookup.
 *
 * @param {HTMLElement} context - Element to search from
 * @param {string} id - Template element ID
 * @returns {DocumentFragment | null}
 */
function cloneTemplate(context, id) {
  const root = context.closest('searchspring-search-results, searchspring-collection') || document;
  const tmpl = root.querySelector(`#${id}`);
  if (tmpl && 'content' in tmpl) {
    return /** @type {HTMLTemplateElement} */ (tmpl).content.cloneNode(true);
  }
  return null;
}

/**
 * Build a single filter value <li> element using <template> cloning.
 *
 * @param {HTMLElement} context - Component element for template lookup
 * @param {string} field - Filter field name
 * @param {{ value: string, label?: string, active?: boolean, count?: string | number }} value - Filter value data
 * @param {(code: string) => string} getColorDisplayName - Color label resolver
 * @param {boolean} showCount - Whether to show the option's count ("Show filter counts" setting)
 * @returns {HTMLLIElement}
 */
function buildFilterValueLi(context, field, value, getColorDisplayName, showCount) {
  const escapedValue = escapeHtml(value.value);
  const escapedLabel = escapeHtml(value.label || '');
  const processedLabel = escapedLabel.includes(':') ? escapedLabel.split(':')[0] : escapedLabel;
  const finalLabel = field === COLORS_FIELD
    ? getColorDisplayName(processedLabel)
    : processedLabel.replace(/_/g, ' ') || escapedLabel;
  const li = document.createElement('li');
  li.className = 'searchspring-filter__value';

  const label = document.createElement('label');
  label.className = 'form-checkbox searchspring-filter__value-label';

  // Checkbox input
  const input = document.createElement('input');
  input.type = 'checkbox';
  input.setAttribute('on:change', '/handleCheckboxChange');
  input.className = 'form-checkbox__input';
  input.value = escapedValue;
  if (value.active) input.checked = true;
  label.appendChild(input);

  // Checkbox icon (from template)
  const checkboxFragment = cloneTemplate(context, 'tmpl-filter-checkbox');
  if (checkboxFragment) {
    label.appendChild(checkboxFragment);
  }

  // Color swatch (if applicable)
  if (field === COLORS_FIELD && value.label?.includes(':')) {
    const swatch = document.createElement('span');
    swatch.className = 'swatch swatch--filter';
    swatch.style.setProperty('--swatch-background', value.label.split(':')[1]);
    label.appendChild(swatch);
  } else if (field === COLOR_GROUPS_FIELD) {
    const swatch = document.createElement('span');
    swatch.className = 'swatch swatch--filter';
    swatch.style.setProperty('--swatch-background', processedLabel.toLowerCase());
    label.appendChild(swatch);
  }

  // Text label + count
  const labelSpan = document.createElement('span');
  labelSpan.className = 'form-checkbox__label flex-auto flex justify-between overflow-hidden';

  const nameSpan = document.createElement('span');
  nameSpan.className = 'overflow-hidden text-ellipsis';
  nameSpan.innerHTML = finalLabel;
  labelSpan.appendChild(nameSpan);

  if (showCount) {
    const countSpan = document.createElement('span');
    countSpan.textContent = String(value.count ?? '');
    labelSpan.appendChild(countSpan);
  }

  label.appendChild(labelSpan);
  li.appendChild(label);

  return li;
}

class SearchspringSearchFilters extends Component {
  filterData = [];
  collectionMenuConfig = [];
  filterConfig = [];
  colorLabels = {};
  /** Lowercase color code → its place in data-color-labels, which lists colors in the admin's color order. */
  colorOrder = new Map();
  isInitialized = false;
  isMobile = false;
  isCollectionsOpen = false;
  featuredFiltersCount = 0;

  async connectedCallback() {
    await super.connectedCallback();
    this.loadFilterConfig();
    this.loadMenuConfig();
    this.loadColorLabels();
    this.loadFeaturedFiltersCount();
  }

  loadFilterConfig() {
    const configAttr = this.getAttribute('data-filter-config');
    const menuConfigAttr = this.getAttribute('data-collection-menu-config');
    if (configAttr) {
      try {
        this.filterConfig = JSON.parse(configAttr);
      } catch (e) {
        console.warn('Failed to parse filter config:', e);
        this.filterConfig = [];
      }
    }
  }

  loadMenuConfig() {
    const menuConfigAttr = this.getAttribute('data-collection-menu-config');
    if (menuConfigAttr) {
      try {
        this.collectionMenuConfig = JSON.parse(menuConfigAttr);
      } catch (e) {
        console.warn('Failed to parse collection menu config:', e);
        this.collectionMenuConfig = [];
      }
    }
  }

  loadFeaturedFiltersCount() {
    const attr = this.getAttribute('data-featured-filters');
    if (attr) {
      const n = parseInt(attr, 10);
      if (!isNaN(n) && n > 0) this.featuredFiltersCount = n;
    }
  }

  /**
   * Parse color labels from data-color-labels attribute.
   */
  loadColorLabels() {
    const attr = this.getAttribute('data-color-labels');
    if (!attr) return;
    try {
      const arr = JSON.parse(attr);
      arr.forEach(item => {
        const colonIndex = item.indexOf(':');
        if (colonIndex > 0) {
          const code = item.substring(0, colonIndex).toLowerCase();
          const label = item.substring(colonIndex + 1);
          this.colorLabels[code] = label;
          if (!this.colorOrder.has(code)) this.colorOrder.set(code, this.colorOrder.size);
        }
      });
    } catch (e) {
      console.warn('Failed to parse color labels:', e);
    }
  }

  setData(data) {
    const filteredData = this.generateFiltersData(data);
    this.filterData = filteredData;
    this.isMobile = this.closest('searchspring-search-results, searchspring-collection')?.isMobile || false;
    this.updateFilters();
    this.isInitialized = true;
  }

  updateFilters() {
    if (!this.isInitialized) {
      this.renderFiltersList();
      this.renderFilters();
    } else {
      this.rerenderFilters();
    }
  }

  rerenderFilters() {
    this.addMissingFilters();
    const filters = this.querySelectorAll('searchspring-filter');
    // Which option had focus, so it can be restored once every filter has been rebuilt.
    const focusOwner = [...filters].find(f => f.hasFocusedOption?.());
    const focusedValue = focusOwner?.focusedOptionValue?.() ?? null;

    filters.forEach(filterElement => {
      filterElement.rerender(this.filterData.find(f => f.field === filterElement.data.field));
      filterElement.isMobile = this.isMobile;
    });
    this.updateOverflowToggle();
    this.updateClearAllLink();

    // Only now is it safe: updateOverflowToggle() can apply display:none to a filter that
    // just lost its last active value, which would blur whatever rerender() had focused.
    if (focusedValue != null) focusOwner?.restoreFocus(focusedValue);
  }

  renderFiltersList() {
    // A <div>, not a <ul>: the children are <searchspring-filter> custom elements, which
    // a <ul> may not contain. Marked up as a list it failed axe's `list` rule (WCAG 1.3.1)
    // and exposed a broken list to assistive tech. A row of filter triggers is not a list.
    const list = document.createElement('div');
    list.className = 'searchspring-filters-list';
    this.innerHTML = '';
    this.appendChild(list);
  }

  renderFilters() {
    const filtersList = this.querySelector('.searchspring-filters-list');
    if (!filtersList) return;

    filtersList.innerHTML = '';

    if (this.collectionMenuConfig.length > 0) {
      const collectionFilter = document.createElement('searchspring-filter');
      collectionFilter.data = {
        field: 'collections',
        label: 'Collections',
        values: this.collectionMenuConfig.map(c => ({ value: c.url, label: c.title, active: false, count: '' }))
      };
      collectionFilter.isMobile = this.isMobile;
      filtersList.appendChild(collectionFilter);
    }

    this.filterData.forEach(filter => filtersList.appendChild(this.createFilter(filter)));

    this.markOverflow(filtersList);
    this.updateOverflowToggle();
    this.updateClearAllLink();
  }

  /**
   * Adds a filter for each facet the panel hasn't drawn yet, where filterData puts it.
   * Only the first draw creates filters, and it can lack some: a zero-result page has no
   * facets, so a shared URL with a color no style has in stock drew an empty panel that
   * the Color list, arriving later, never filled.
   */
  addMissingFilters() {
    const filtersList = this.querySelector('.searchspring-filters-list');
    if (!filtersList) return;

    const drawn = new Map(
      [...filtersList.querySelectorAll(':scope > searchspring-filter')].map(el => [el.data?.field, el])
    );
    if (this.filterData.every(filter => drawn.has(filter.field))) return;

    // Backwards, so each new filter goes right before the one filterData lists after it.
    let next = filtersList.querySelector(':scope > .searchspring-filters-toggle-li');
    [...this.filterData].reverse().forEach(filter => {
      next = drawn.get(filter.field) ?? filtersList.insertBefore(this.createFilter(filter), next);
    });

    this.markOverflow(filtersList);
  }

  createFilter(filter) {
    const filterComponent = document.createElement('searchspring-filter');
    filterComponent.data = filter;
    filterComponent.isMobile = this.isMobile;
    filterComponent.colorLabels = this.colorLabels;
    filterComponent.colorOrder = this.colorOrder;
    return filterComponent;
  }

  /**
   * Puts the filters past the featured count (data-featured-filters) behind the
   * "All filters" toggle, adding the toggle the first time there are any.
   */
  markOverflow(filtersList) {
    const n = this.featuredFiltersCount;
    if (n <= 0) return;

    const filters = [...filtersList.querySelectorAll(':scope > searchspring-filter')];
    filters.forEach((filter, index) => {
      if (index >= n) filter.setAttribute('data-overflow', 'true');
      else filter.removeAttribute('data-overflow');
    });
    if (filters.length <= n || filtersList.querySelector(':scope > .searchspring-filters-toggle-li')) return;

    this.setAttribute('data-overflow-expanded', 'false');
    const toggleLi = document.createElement('div');
    toggleLi.className = 'searchspring-filters-toggle-li';
    const btn = document.createElement('button');
    btn.className = 'searchspring-filters-toggle';
    btn.setAttribute('aria-expanded', 'false');
    btn.setAttribute('on:click', '/handleToggleOverflow');
    const textSpan = document.createElement('span');
    textSpan.textContent = 'All filters';
    btn.appendChild(textSpan);
    const caretFragment = cloneTemplate(this, 'tmpl-filter-caret');
    if (caretFragment) btn.appendChild(caretFragment);
    toggleLi.appendChild(btn);
    filtersList.appendChild(toggleLi);
  }

  generateFiltersData(data) {
    if (this.filterConfig.length === 0) {
      return data;
    }

    const configFields = this.filterConfig.map(c => c.field);
    const labelMap = new Map(this.filterConfig.map(c => [c.field, c.label]));

    const filtered = data.filter(item => configFields.includes(item.field));

    filtered.sort((a, b) => {
      return configFields.indexOf(a.field) - configFields.indexOf(b.field);
    });

    return filtered.map(item => ({
      ...item,
      label: labelMap.get(item.field) || item.label
    }));
  }

  updateOverflowToggle() {
    const toggleLi = this.querySelector('.searchspring-filters-toggle-li');
    if (!toggleLi) return;

    const overflowFilters = [...this.querySelectorAll('searchspring-filter[data-overflow="true"]')];
    if (!overflowFilters.length) return;

    // Sync data-has-active attribute on each overflow filter
    overflowFilters.forEach(el => {
      const hasActive = el.data?.values?.some(v => v.active) ?? false;
      if (hasActive) {
        el.setAttribute('data-has-active', 'true');
      } else {
        el.removeAttribute('data-has-active');
      }
    });

    const allActive = overflowFilters.every(el => el.hasAttribute('data-has-active'));

    if (allActive) {
      // All overflow filters are already visible via data-has-active; button not needed
      toggleLi.hidden = true;
    } else if (this.getAttribute('data-overflow-expanded') === 'false') {
      // Some overflow filters are still hidden; make sure button is visible
      toggleLi.hidden = false;
    }
    // else: user already expanded — keep button hidden
  }

  handleToggleOverflow(event) {
    event.preventDefault();
    this.setAttribute('data-overflow-expanded', 'true');
    const toggleLi = this.querySelector('.searchspring-filters-toggle-li');
    if (!toggleLi) return;

    const button = toggleLi.querySelector('.searchspring-filters-toggle');
    const hadFocus = button?.contains(document.activeElement);
    toggleLi.hidden = true;

    // The button removes itself, so focus would fall to <body> and strand a keyboard user
    // at the top of the document right after they revealed the extra filters.
    if (hadFocus) {
      const revealed = this.querySelector('searchspring-filter[data-overflow="true"] .searchspring-filter__label');
      if (revealed instanceof HTMLElement) revealed.focus();
    }
  }

  handleClearAllFilters(event) {
    event.preventDefault();
    const resultsComponent = this.closest('searchspring-search-results, searchspring-collection');
    if (resultsComponent && typeof resultsComponent.onClearAllFilters === 'function') {
      resultsComponent.onClearAllFilters();
    }
  }

  updateClearAllLink() {
    let clearAllLi = this.querySelector(':scope > .clear-all-li');
    const hasActive = this.filterData.some(f => f.values.some(v => v.active));

    if (hasActive && !clearAllLi) {
      clearAllLi = document.createElement('div');
      clearAllLi.className = 'clear-all-li';

      const link = document.createElement('a');
      link.href = '#';
      link.setAttribute('on:click', '/handleClearAllFilters');
      link.className = 'clear-all-link';
      link.textContent = 'Clear all';

      clearAllLi.appendChild(link);
      this.appendChild(clearAllLi);
    } else if (!hasActive && clearAllLi) {
      clearAllLi.remove();
    }
  }
}

class Filter extends Component {
  /** Whether options show their counts: the section's "Show filter counts" setting. */
  get showCounts() {
    return this.closest('searchspring-search-filters')?.getAttribute('data-show-counts') === 'true';
  }

  isOpen = false;
  data = null;
  isMobile = false;
  colorLabels = {};
  colorOrder = new Map();

  /**
   * Unique id of this filter's dropdown, referenced by the trigger's aria-controls.
   * @type {string}
   */
  dropdownId = '';

  getColorDisplayName(code) {
    const handle = code.toLowerCase();
    return this.colorLabels[handle] || code.replace(/_/g, ' ');
  }

  async connectedCallback() {
    await super.connectedCallback();
    this.render();
    document.addEventListener('click', this.outerClickHandler);
    this.addEventListener('keydown', this.handleKeyDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.outerClickHandler);
    this.removeScrollListeners();
  }

  /**
   * Opens or closes the dropdown, keeping the CSS hook and the ARIA state in sync so they
   * cannot drift apart. Closing also takes the panel out of the tab order (see the
   * `visibility` rules in search-results.css).
   *
   * @param {boolean} open
   */
  setOpen(open) {
    this.isOpen = open;
    this.setAttribute('data-open', String(open));
    this.querySelector('.searchspring-filter__label')?.setAttribute('aria-expanded', String(open));
    if (!open) this.removeScrollListeners();
  }

  /**
   * Escape closes the open dropdown and returns focus to its trigger, as the disclosure
   * pattern requires. Propagation stops so that the innermost layer closes first, once the
   * mobile filter panel grows its own Escape handling — today it has none, so a keyboard
   * user still has no way to dismiss that panel.
   *
   * @param {KeyboardEvent} event
   */
  handleKeyDown = (event) => {
    if (event.key !== 'Escape' || this.getAttribute('data-open') !== 'true') return;

    event.stopPropagation();
    this.setOpen(false);
    /** @type {HTMLElement | null} */ (this.querySelector('.searchspring-filter__label'))?.focus();
  };

  outerClickHandler = (event) => {
    // Checking an option rebuilds the list, so this document-level handler can run after
    // the clicked node has been detached. A detached node is not a click "outside" —
    // without this guard the dropdown closes itself every time a filter is applied.
    if (!(event.target instanceof Node) || !event.target.isConnected) return;

    const isOuterClick = !this.contains(event.target);
    if (isOuterClick) {
      // Check if the click is inside another searchspring-filter
      const clickedFilter = event.target.closest?.('searchspring-filter');
      if (clickedFilter && clickedFilter !== this) {
        // Clicked on another filter, don't close this one
        return;
      }
      this.setOpen(false);
      return;
    }
  };

  handleClickFilterLabel(event) {
    const isOpen = this.getAttribute('data-open') === 'true';
    if (isOpen) {
      this.setOpen(false);
      return;
    }

    if (!this.isMobile) {
      const filters = document.querySelectorAll('searchspring-filter');
      filters.forEach(filter => {
        if (filter !== this) filter.setOpen?.(false);
      });
      this.positionDropdown();
      this.addScrollListeners();
    }

    this.setOpen(true);
  }

  scrollRepositionHandler = () => {
    this.positionDropdown();
  };

  addScrollListeners() {
    window.addEventListener('scroll', this.scrollRepositionHandler, { passive: true });
    const filterList = this.closest('.searchspring-filters-list');
    if (filterList) filterList.addEventListener('scroll', this.scrollRepositionHandler, { passive: true });
  }

  removeScrollListeners() {
    window.removeEventListener('scroll', this.scrollRepositionHandler);
    const filterList = this.closest('.searchspring-filters-list');
    if (filterList) filterList.removeEventListener('scroll', this.scrollRepositionHandler);
  }

  positionDropdown() {
    const label = this.querySelector('.searchspring-filter__label');
    const dropdown = this.querySelector('.searchspring-filter__dropdown');
    if (!label || !dropdown) return;
    const rect = label.getBoundingClientRect();
    dropdown.style.top = `${rect.bottom - 1}px`;
    dropdown.style.left = `${rect.left}px`;
  }

  handleCheckboxChange(event) {
    const checkbox = event.target;
    const isChecked = checkbox.checked;
    const fieldName = this.data.field;
    const fieldValue = checkbox.value;
    const resultsComponent = this.closest('searchspring-search-results, searchspring-collection');
    if (resultsComponent && typeof resultsComponent.onChangeFilter === 'function') {
      resultsComponent.onChangeFilter(fieldName, fieldValue, isChecked);
    }
  }

  handleClearAll(event) {
    event.preventDefault();
    if (this.data.field === 'collections') return;

    const checkboxes = this.querySelectorAll('input[type="checkbox"]');
    checkboxes.forEach(cb => {
      if (cb.checked) {
        cb.checked = false;
      }
    });
    const resultsComponent = this.closest('searchspring-search-results, searchspring-collection');
    if (resultsComponent && typeof resultsComponent.onClearAllFilter === 'function') {
      resultsComponent.onClearAllFilter(this.data.field);
    }
  }

  /**
   * Build the filter trigger (shared by collections and standard filters).
   *
   * A real <button> so it is reachable by Tab and operable with Enter/Space, carrying the
   * disclosure state that tells assistive tech whether its options are showing.
   *
   * @param {boolean} isCollections
   * @returns {HTMLButtonElement}
   */
  buildFilterLabel(isCollections) {
    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'searchspring-filter__label';
    trigger.setAttribute('on:click', '/handleClickFilterLabel');
    trigger.setAttribute('aria-expanded', String(this.isOpen));
    trigger.setAttribute('aria-controls', this.dropdownId);

    if (!isCollections) {
      const indicator = document.createElement('span');
      const hasActive = this.data.values.some(v => v.active);
      indicator.className = `searchspring-filter__indicator${hasActive ? ' searchspring-filter__indicator--active' : ''}`;
      trigger.appendChild(indicator);
    }

    const textSpan = document.createElement('span');
    textSpan.textContent = this.data.label;
    trigger.appendChild(textSpan);

    const caretFragment = cloneTemplate(this, 'tmpl-filter-caret');
    if (caretFragment) {
      trigger.appendChild(caretFragment);
    }

    return trigger;
  }

  /**
   * Build the <ul> of filter values using DOM APIs and <template> cloning.
   * Applies numeric sorting for Size fields.
   * @returns {HTMLUListElement}
   */
  buildValuesList() {
    const dropdown = document.createElement('div');
    dropdown.className = 'searchspring-filter__dropdown';
    dropdown.id = this.dropdownId;

    const ul = document.createElement('ul');
    ul.className = 'searchspring-filter__values';

    const sortedValues = sortFilterValues(this.data.field, this.data.values, this.colorOrder);

    sortedValues.forEach(value => {
      const li = buildFilterValueLi(
        this,
        this.data.field,
        value,
        (code) => this.getColorDisplayName(code),
        this.showCounts
      );
      ul.appendChild(li);
    });

    dropdown.appendChild(ul);

    if (this.data.values.some(v => v.active)) {
      const clearDiv = document.createElement('div');
      clearDiv.className = 'searchspring-filter__clear-all';

      const clearLink = document.createElement('a');
      clearLink.href = '#';
      clearLink.setAttribute('on:click', '/handleClearAll');
      clearLink.className = 'clear-all-link';
      clearLink.textContent = 'Clear all';

      clearDiv.appendChild(clearLink);
      dropdown.appendChild(clearDiv);
    }

    return dropdown;
  }

  render() {
    if (!this.data) return;

    this.dropdownId ||= `searchspring-filter-dropdown-${(filterInstanceCount += 1)}`;
    this.setAttribute('data-open', this.isOpen.toString());

    const wrapper = document.createElement('div');
    wrapper.className = this.data.field === 'collections'
      ? 'searchspring-filter searchspring-filter--collections'
      : 'searchspring-filter';

    const trigger = this.buildFilterLabel(this.data.field === 'collections');
    wrapper.appendChild(trigger);

    if (this.data.field === 'collections') {
      // Collections render as links, not checkboxes
      const dropdown = document.createElement('div');
      dropdown.className = 'searchspring-filter__dropdown';
      dropdown.id = this.dropdownId;

      const ul = document.createElement('ul');
      ul.className = 'searchspring-filter__values';
      this.data.values.forEach(value => {
        const li = document.createElement('li');
        li.className = 'searchspring-filter__value';

        const link = document.createElement('a');
        link.href = value.value;
        link.className = 'searchspring-collection__link';
        link.textContent = value.label;

        li.appendChild(link);
        ul.appendChild(li);
      });
      dropdown.appendChild(ul);
      wrapper.appendChild(dropdown);
    } else {
      wrapper.appendChild(this.buildValuesList());
    }

    this.innerHTML = '';
    this.appendChild(wrapper);
  }

  rerender(newData) {
    if (!newData) return;

    if (this.data.field === 'collections') {
      // Collections don't need rerendering as they are static
      return;
    }

    this.data = newData;

    const indicator = this.querySelector('.searchspring-filter__indicator');
    if (indicator) {
      const hasActive = this.data.values.some(v => v.active);
      indicator.className = `searchspring-filter__indicator${hasActive ? ' searchspring-filter__indicator--active' : ''}`;
    }

    const valueContainers = this.querySelectorAll('.searchspring-filter__values');
    valueContainers.forEach(valueContainer => {
      // Clear existing children
      valueContainer.innerHTML = '';

      // Rebuild sorted values using the shared builder
      const sortedValues = sortFilterValues(this.data.field, this.data.values, this.colorOrder);
      sortedValues.forEach(value => {
        const li = buildFilterValueLi(
          this,
          this.data.field,
          value,
          (code) => this.getColorDisplayName(code),
          this.showCounts
        );
        valueContainer.appendChild(li);
      });

      // Handle clear-all - outside the scrollable ul, inside the dropdown wrapper
      const dropdownWrapper = valueContainer.closest('.searchspring-filter__dropdown');
      if (dropdownWrapper) {
        let clearDiv = dropdownWrapper.querySelector('.searchspring-filter__clear-all');
        const hasActive = this.data.values.some(v => v.active);
        if (hasActive && !clearDiv) {
          clearDiv = document.createElement('div');
          clearDiv.className = 'searchspring-filter__clear-all';

          const clearLink = document.createElement('a');
          clearLink.href = '#';
          clearLink.setAttribute('on:click', '/handleClearAll');
          clearLink.className = 'clear-all-link';
          clearLink.textContent = 'Clear all';

          clearDiv.appendChild(clearLink);
          dropdownWrapper.appendChild(clearDiv);
        } else if (!hasActive && clearDiv) {
          clearDiv.remove();
        }
      }
    });

  }

  /**
   * The value of the option the visitor currently has focused, or null if focus is
   * elsewhere on the page.
   *
   * @returns {string | null}
   */
  focusedOptionValue() {
    const active = document.activeElement;
    if (!(active instanceof HTMLInputElement) || !this.contains(active)) return null;
    return active.value;
  }

  /**
   * Whether the visitor currently has one of this filter's options focused.
   * @returns {boolean}
   */
  hasFocusedOption() {
    return this.focusedOptionValue() != null;
  }

  /**
   * Rebuilding the option list detaches whatever the visitor had focused, which drops
   * focus to <body> and sends keyboard users back to the top of the document. Put focus
   * back on the equivalent rebuilt option, or on the trigger if that option is gone.
   *
   * @param {string} value
   */
  restoreFocus(value) {
    const option = this.querySelector(
      `.searchspring-filter__values input[type="checkbox"][value="${CSS.escape(value)}"]`
    );
    const target = option ?? this.querySelector('.searchspring-filter__label');
    if (target instanceof HTMLElement) target.focus({ preventScroll: true });
  }
}

/**
 * Filters an array of objects based on a whitelist of 'field' values.
 *
 * @param {Array<Object>} data The original array of objects.
 * @param {Array<string>} allowedFields An array of string values for the 'field' property to keep.
 * @returns {Array<Object>} A new array containing only the objects whose 'field' property is in the allowedFields list.
 */
function filterArrayByField(data, allowedFields) {
  // Use the Array.prototype.filter() method to create a new array
  // containing all elements that pass the test implemented by the provided function.
  return data.filter(item => {
    // Check if the 'item.field' value is present in the allowedFields array.
    return allowedFields.includes(item.field);
  });
}

if (!customElements.get('searchspring-search-filters')) {
  customElements.define('searchspring-search-filters', SearchspringSearchFilters);
}

if (!customElements.get('searchspring-filter')) {
  customElements.define('searchspring-filter', Filter);
}
